import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Play, Pause, Battery, Gift, Clock, ShoppingCart, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { autoFaucetApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useTelegram, showRewardedAd } from '../../hooks';
import toast from 'react-hot-toast';

// Enhanced logger utility with backend reporting for Railway monitoring
const API_URL = import.meta.env.VITE_API_URL || '/api';

const logger = {
  _send: async (level: string, msg: string, data?: Record<string, unknown>) => {
    try {
      await fetch(`${API_URL}/logs/frontend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level,
          message: msg,
          data,
          component: 'AutoFaucet',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      });
    } catch {
      // Silent fail
    }
  },
  info: (msg: string, data?: Record<string, unknown>) => {
    console.log(`[AutoFaucet] ${msg}`, data || '');
    // Only send important info to backend
  },
  warn: (msg: string, data?: Record<string, unknown>) => {
    console.warn(`[AutoFaucet] ${msg}`, data || '');
    logger._send('warn', msg, data);
  },
  error: (msg: string, data?: Record<string, unknown>) => {
    console.error(`[AutoFaucet] ${msg}`, data || '');
    logger._send('error', msg, data);
  },
  debug: (msg: string, data?: Record<string, unknown>) => {
    if (import.meta.env.DEV) {
      console.log(`[AutoFaucet DEBUG] ${msg}`, data || '');
    }
  },
};

interface EnergyPackage {
  _id: string;
  name: string;
  description?: string;
  price: number;
  energyGranted: number;
  badge?: string;
}

export function AutoFaucet() {
  const [isActive, setIsActive] = useState(false);
  const [energy, setEnergy] = useState(0);
  const [maxEnergy, setMaxEnergy] = useState(100);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [todayCycles, setTodayCycles] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [earnedThisSession, setEarnedThisSession] = useState(0);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(60);
  
  // Error state for component-level error handling
  const [componentError, setComponentError] = useState<string | null>(null);
  
  // Energy package purchase state
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [packages, setPackages] = useState<EnergyPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  
  // Session config from backend (all dynamic from admin settings)
  const [sessionDuration, setSessionDuration] = useState<number | null>(null); // seconds
  const [adsPerSession, setAdsPerSession] = useState<number | null>(null);
  const [rewardPerCycle, setRewardPerCycle] = useState<number | null>(null);
  const [adsShownThisSession, setAdsShownThisSession] = useState(0);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(isActive);
  const processingSessionEnd = useRef(false); // Prevent duplicate processSessionEnd calls
  
  const updateBalance = useAppStore((state) => state.updateBalance);
  const user = useAppStore((state) => state.user);
  const { hapticFeedback, hapticNotification } = useTelegram();
  
  // Get energy per ad from config
  const energyPerAd = useConfigStore((state) => state.faucet.energyPerAd);

  // Keep ref in sync with state
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Load energy packages when modal opens
  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const result = await autoFaucetApi.getEnergyPackages();
      setPackages(result.packages || []);
    } catch (error) {
      console.error('Failed to load energy packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  // Purchase energy package
  const purchasePackage = async (pkg: EnergyPackage) => {
    if (!user || user.balance < pkg.price) {
      toast.error('Insufficient balance');
      return;
    }

    setPurchasingId(pkg._id);
    hapticFeedback('medium');

    try {
      const result = await autoFaucetApi.purchaseEnergyPackage(pkg._id);
      
      if (result.success) {
        setEnergy(result.newEnergy);
        updateBalance(result.newBalance);
        hapticNotification('success');
        toast.success(`+${result.energyAdded} energy purchased!`);
        setShowPackageModal(false);
      }
    } catch (error: any) {
      hapticNotification('error');
      toast.error(error.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasingId(null);
    }
  };

  // Open package modal and load packages
  const openPackageModal = () => {
    setShowPackageModal(true);
    loadPackages();
  };

  // Create ref to maintain sessionDuration throughout the session
  const sessionDurationRef = useRef<number>(0);
  
  // Ad interval from backend (admin configurable, e.g., 10 = show ad every 10 seconds)
  const [adInterval, setAdInterval] = useState<number>(10);

  // Load admin settings once on component mount (before user can start session)
  useEffect(() => {
    let isMounted = true;

    const loadStatus = async () => {
      try {
        logger.info('Loading AutoFaucet status...', { userId: user?.telegramId });
        const status = await autoFaucetApi.getStatus();
        
        if (!isMounted) return;
        
        setIsActive(status.isActive);
        setEnergy(status.energy);
        setMaxEnergy(status.maxEnergy);
        setTodayCycles(status.todayCycles);
        
        // Load ALL session configuration from backend (admin settings) - REQUIRED, no fallbacks
        const duration = status.sessionDuration;
        const ads = status.adsPerSession;
        const reward = status.earnPerCycle;
        const interval = status.adInterval; // Ad interval in seconds (e.g., 10 = every 10 seconds)
        
        setSessionDuration(duration);
        sessionDurationRef.current = duration; // Save to ref for timer to use
        setAdsPerSession(ads);
        setRewardPerCycle(reward);
        
        // Set ad interval from admin settings (takes priority over calculated value)
        if (interval && interval > 0) {
          setAdInterval(interval);
        } else if (duration && ads) {
          // Fallback: calculate from session duration and ads per session
          setAdInterval(Math.floor(duration / ads));
        }
        
        // Set initial timer to loaded session duration
        if (!status.isActive) {
          setTimeRemaining(duration);
        }
        
        // Clear any previous error
        setComponentError(null);
        
        logger.info('AutoFaucet settings loaded from admin dashboard:', { 
          duration, 
          ads, 
          reward,
          adInterval: interval || Math.floor(duration / ads)
        });
        setIsLoadingConfig(false);
      } catch (error: unknown) {
        const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
        const errorMessage = err.response?.data?.error || err.message || 'Failed to load auto faucet';
        const statusCode = err.response?.status;
        
        logger.error('Failed to fetch auto faucet status', {
          error: errorMessage,
          statusCode,
          userId: user?.telegramId,
          isFraudBlocked: statusCode === 403 || statusCode === 429,
        });
        
        // Set user-friendly error message
        if (statusCode === 403) {
          setComponentError('Your account has been restricted. Please contact support.');
        } else if (statusCode === 429) {
          setComponentError('Too many requests. Please wait a moment and try again.');
        } else if (statusCode === 401) {
          setComponentError('Session expired. Please refresh the page.');
        } else {
          setComponentError(errorMessage);
        }
        
        setIsLoadingConfig(false);
      }
    };

    loadStatus();

    return () => {
      isMounted = false;
    };
  }, [user?.telegramId]); // Re-run if user changes

  // Watch rewarded ad to earn energy - now uses session tokens for fraud prevention
  const watchAdForEnergy = useCallback(async () => {
    setIsWatchingAd(true);
    hapticFeedback('medium');
    
    let sessionToken: string | null = null;
    
    try {
      // Step 1: Start ad session on server BEFORE showing ad
      try {
        const sessionResult = await autoFaucetApi.startAdSession();
        if (!sessionResult.success || !sessionResult.sessionToken) {
          throw new Error('Failed to start ad session');
        }
        sessionToken = sessionResult.sessionToken;
      } catch (sessionError: any) {
        console.error('Failed to start ad session:', sessionError);
        toast.error(sessionError.response?.data?.error || 'Failed to start. Please try again.');
        setIsWatchingAd(false);
        return;
      }
      
      // Step 2: Show Adsgram rewarded ad only
      const adShown = await showRewardedAd();
      
      if (adShown && sessionToken) {
        // Step 3: Call backend to earn energy with session token
        const result = await autoFaucetApi.earnEnergyFromAd(sessionToken);
        
        if (result.success) {
          setEnergy(result.newEnergy);
          hapticNotification('success');
          toast.success(`+${result.energyAdded} energy earned!`);
        }
      } else if (!adShown) {
        toast.error('Ad not available. Try again later.');
      }
    } catch (error: any) {
      console.error('Watch ad for energy error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to earn energy';
      toast.error(errorMsg);
    } finally {
      setIsWatchingAd(false);
    }
  }, [hapticFeedback, hapticNotification]);

  // Show Adsgram ad only (Onclicka available as fallback in other components)
  const showSessionAd = useCallback(async (): Promise<boolean> => {
    // Only show Adsgram rewarded ads
    const success = await showRewardedAd();
    return success;
  }, []);

  // Stop auto faucet function (defined early for use in effects)
  const doStopAutoFaucet = useCallback(async (showAd: boolean = false) => {
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      await autoFaucetApi.stop();
    } catch (error) {
      console.error('Failed to stop:', error);
    }

    // Show ad when stopping (if requested and earned something)
    if (showAd && earnedThisSession > 0) {
      toast('Watch an ad to collect your earnings...', { icon: '📺' });
      await showRewardedAd();
    }

    setIsActive(false);
    setSessionToken(null);
    // Reset timer to loaded duration using ref
    if (sessionDurationRef.current) {
      setTimeRemaining(sessionDurationRef.current);
    }
    setIsPaused(false);
    setAdsShownThisSession(0);
  }, [earnedThisSession]);

  // Handle visibility change - stop auto faucet when app is minimized
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden && isActiveRef.current) {
        console.log('App minimized - stopping auto faucet');
        await doStopAutoFaucet(false);
        toast('Auto faucet stopped - app minimized', { icon: '⏸️' });
      }
    };

    // Telegram WebApp events
    const handleTelegramHide = async () => {
      if (isActiveRef.current) {
        console.log('Telegram app hidden - stopping auto faucet');
        await doStopAutoFaucet(false);
        toast('Auto faucet stopped - app minimized', { icon: '⏸️' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for Telegram WebApp events
    const tg = (window as any).Telegram?.WebApp;
    if (tg) {
      tg.onEvent?.('viewportChanged', (event: { isStateStable: boolean }) => {
        if (!event.isStateStable && isActiveRef.current) {
          handleTelegramHide();
        }
      });
    }

    window.addEventListener('beforeunload', handleTelegramHide);
    window.addEventListener('pagehide', handleTelegramHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleTelegramHide);
      window.removeEventListener('pagehide', handleTelegramHide);
    };
  }, [doStopAutoFaucet]);

  // Check if it's time to show an ad (at equally spaced intervals)
  const shouldShowAd = useCallback((currentTime: number): boolean => {
    if (!adsPerSession || adsPerSession <= 0) return false;
    if (adInterval <= 0) return false;
    
    const timeElapsed = sessionDurationRef.current - currentTime;
    // Calculate which ad interval we're in
    // For example: 30s session, 3 ads = show at 10s, 20s, 30s elapsed
    const expectedAdsShown = Math.floor(timeElapsed / adInterval);
    
    return expectedAdsShown > adsShownThisSession;
  }, [adsPerSession, adInterval, adsShownThisSession]);

  // Process ad at interval
  const processIntervalAd = useCallback(async (): Promise<boolean> => {
    setIsPaused(true);
    
    try {
      toast('Watch ad to continue...', { icon: '📺', duration: 2000 });
      const adSuccess = await showSessionAd();
      
      if (!adSuccess) {
        toast.error('Ad required to continue. Stopping auto faucet.');
        await doStopAutoFaucet(false);
        return false;
      }
      
      setAdsShownThisSession(prev => prev + 1);
      setIsPaused(false);
      return true;
    } catch (error) {
      console.error('Failed to show interval ad:', error);
      await doStopAutoFaucet(false);
      return false;
    }
  }, [showSessionAd, doStopAutoFaucet]);

  // Process session completion (when timer reaches 0)
  // Automatically: 1) Get reward 2) Show ad 3) Start new session - no manual interaction
  const processSessionEnd = useCallback(async () => {
    if (!sessionToken) return;
    
    setIsPaused(true);
    
    try {
      // Step 1: Call the completeCycle endpoint to get reward
      const result = await autoFaucetApi.completeCycle(sessionToken);
      
      logger.info('Cycle completed:', result);
      
      if (result.success && result.reward !== undefined && result.reward > 0) {
        // Reward successfully earned from server
        const rewardAmount = result.reward;
        setEarnedThisSession((prev) => prev + rewardAmount);
        
        if (result.cyclesCompletedToday !== undefined) {
          setTodayCycles(result.cyclesCompletedToday);
        } else {
          setTodayCycles((prev) => prev + 1);
        }
        
        // Update balance with server-returned value
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        }
        
        setEnergy(result.energy);
        hapticNotification('success');
        toast.success(`+${rewardAmount} points!`, { duration: 1200 });
        logger.info('Reward credited:', { reward: rewardAmount, energy: result.energy });
      }

      // Update session config if provided
      if (result.sessionDuration) {
        sessionDurationRef.current = result.sessionDuration;
        setSessionDuration(result.sessionDuration);
      }
      if (result.adsPerSession !== undefined) {
        setAdsPerSession(result.adsPerSession);
      }

      // Step 2: Check if user can continue (has energy)
      if (result.canStartNewSession && result.energy >= 1) {
        // Automatically show ad and start new session
        logger.info('Auto-showing ad for next cycle...');
        
        // Show the ad automatically
        const adSuccess = await showRewardedAd();
        
        // Step 3: Start new session regardless of ad result (if ads are disabled or fail, still continue)
        // This ensures the auto-faucet keeps running
        try {
          const startResult = await autoFaucetApi.start();
          
          if (startResult.success) {
            // New session started automatically
            setSessionToken(startResult.sessionToken);
            setEnergy(startResult.energy);
            setTimeRemaining(sessionDurationRef.current);
            setAdsShownThisSession(0);
            setIsPaused(false);
            setIsActive(true);
            processingSessionEnd.current = false;
            
            logger.info(`New cycle started automatically, duration: ${sessionDurationRef.current}s`);
            
            if (!adSuccess) {
              // Ad didn't show but we continue anyway
              logger.warn('Ad failed but continuing with new cycle');
            }
            return;
          }
        } catch (startError: any) {
          logger.error('Failed to auto-start new session:', startError);
          // If start fails, stop the auto faucet
          toast.error(startError.response?.data?.error || 'Failed to continue');
        }
      } else if (result.message) {
        toast(result.message, { icon: '⚡' });
      }

      // Session ended (no energy or start failed)
      setIsActive(false);
      setSessionToken(null);
      setTimeRemaining(sessionDurationRef.current);
      setIsPaused(false);
      setAdsShownThisSession(0);
      processingSessionEnd.current = false;
      
      logger.info('Auto faucet stopped', { reason: result.message || 'Out of energy' });
      
    } catch (error: any) {
      console.error('Cycle processing failed:', error);
      processingSessionEnd.current = false;
      
      // Reset state on error
      setIsActive(false);
      setSessionToken(null);
      setTimeRemaining(sessionDurationRef.current);
      setIsPaused(false);
      setAdsShownThisSession(0);
      
      toast.error(error.response?.data?.error || 'Session error');
    }
  }, [sessionToken, updateBalance, hapticNotification]);

  // Handle session end when timer reaches 0 (separate from state update)
  useEffect(() => {
    if (timeRemaining <= 0 && isActive && !isPaused && sessionToken && !processingSessionEnd.current) {
      processingSessionEnd.current = true; // Prevent duplicate calls
      logger.info('Session timer reached 0, processing cycle completion');
      processSessionEnd(); // Call outside of setState
    }
  }, [timeRemaining, isActive, isPaused, sessionToken, processSessionEnd]);

  // Timer countdown effect with ad interval checking
  useEffect(() => {
    if (isActive && !isPaused && sessionToken) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          const nextTime = prev - 1;
          
          // Check if we should show an ad at this interval
          if (shouldShowAd(nextTime) && nextTime > 1) {
            processIntervalAd();
            return nextTime;
          }
          
          // Don't reset here - let the separate effect handle completion
          // When timeRemaining <= 0, the effect will trigger processSessionEnd
          return Math.max(nextTime, 0); // Stop at 0, don't go negative
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [isActive, isPaused, sessionToken, shouldShowAd, processIntervalAd]);

  // Heartbeat effect - ping server every 30 seconds to keep session alive
  useEffect(() => {
    let heartbeatInterval: NodeJS.Timeout | null = null;
    
    if (isActive && sessionToken) {
      heartbeatInterval = setInterval(async () => {
        try {
          const result = await autoFaucetApi.heartbeat(sessionToken);
          
          // Update energy from server
          if (result.energy !== undefined) {
            setEnergy(result.energy);
          }
          
          // If session is no longer active on server, stop locally
          if (!result.isActive || !result.success) {
            logger.info('Heartbeat indicates session inactive, stopping');
            await doStopAutoFaucet(false);
            if (result.message) {
              toast(result.message, { icon: '⚡' });
            }
          }
        } catch (error: any) {
          logger.error('Heartbeat failed:', error);
          // If heartbeat fails repeatedly, stop the session
          if (error.response?.status === 400 || error.response?.status === 404) {
            await doStopAutoFaucet(false);
            toast.error('Session expired - stopped');
          }
        }
      }, 30000); // Every 30 seconds
    }
    
    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isActive, sessionToken, doStopAutoFaucet]);

  const startAutoFaucet = async () => {
    // Ensure all settings are loaded from admin dashboard before starting
    if (!sessionDuration || sessionDuration <= 0) {
      toast.error('Settings not loaded. Please refresh and try again.');
      return;
    }

    if (energy < 1) {
      toast.error('Not enough energy');
      return;
    }

    setIsLoading(true);
    hapticFeedback('medium');

    try {
      // Show Adsgram rewarded ad before starting
      toast('Watch an ad to start auto faucet...', { icon: '📺' });
      const adSuccess = await showRewardedAd();
      
      if (!adSuccess) {
        toast.error('Please watch the ad to start auto faucet');
        setIsLoading(false);
        return;
      }

      const result = await autoFaucetApi.start();
      if (result.success) {
        setIsActive(true);
        setSessionToken(result.sessionToken);
        setEnergy(result.energy);
        setEarnedThisSession(0);
        // Use ref value to ensure consistency with loaded admin settings
        setTimeRemaining(sessionDurationRef.current);
        setAdsShownThisSession(0);
        setIsPaused(false);
        hapticNotification('success');
        logger.info('Auto faucet started', { sessionDuration: sessionDurationRef.current });
        toast.success('Auto faucet started!');
      }
    } catch (error: any) {
      hapticNotification('error');
      toast.error(error.response?.data?.error || 'Failed to start');
    } finally {
      setIsLoading(false);
    }
  };

  const stopAutoFaucet = async () => {
    setIsLoading(true);
    hapticFeedback('light');

    await doStopAutoFaucet(true);

    if (earnedThisSession > 0) {
      toast.success(`Session ended: +${earnedThisSession} points earned`);
    }

    setIsLoading(false);
  };

  const energyPercent = (energy / maxEnergy) * 100;
  const timerPercent = sessionDuration ? (timeRemaining / sessionDuration) * 100 : 0;

  // Format time as mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show loading state while config is being fetched
  if (isLoadingConfig) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-6"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-3"></div>
            <p className="text-white/60 text-sm">Loading admin settings...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  // Show error state if component failed to load
  if (componentError) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-6 bg-red-500/10 border border-red-500/30"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-red-400 mb-2">Auto Faucet Error</h3>
            <p className="text-sm text-white/60 mb-4">{componentError}</p>
            <button
              onClick={() => {
                setComponentError(null);
                setIsLoadingConfig(true);
                // Trigger reload by updating a dependency
                autoFaucetApi.getStatus()
                  .then((status) => {
                    setIsActive(status.isActive);
                    setEnergy(status.energy);
                    setMaxEnergy(status.maxEnergy);
                    setTodayCycles(status.todayCycles);
                    setSessionDuration(status.sessionDuration);
                    sessionDurationRef.current = status.sessionDuration;
                    setAdsPerSession(status.adsPerSession);
                    setRewardPerCycle(status.earnPerCycle);
                    if (status.adInterval && status.adInterval > 0) {
                      setAdInterval(status.adInterval);
                    }
                    setComponentError(null);
                    setIsLoadingConfig(false);
                  })
                  .catch((err) => {
                    logger.error('Retry failed', { error: err.message });
                    setComponentError('Retry failed. Please try again later.');
                    setIsLoadingConfig(false);
                  });
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="card p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isActive 
              ? 'bg-gradient-to-br from-green-500 to-emerald-500 animate-pulse'
              : 'bg-gradient-to-br from-gray-500 to-gray-600'
          }`}>
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Auto Faucet</h3>
            <p className="text-white/60 text-sm">
              {isActive ? (isPaused ? 'Showing ad...' : 'Running...') : 'Passive earning'}
            </p>
          </div>
        </div>

        {/* Status indicator */}
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          isActive 
            ? isPaused 
              ? 'bg-yellow-500/20 text-yellow-400'
              : 'bg-green-500/20 text-green-400'
            : 'bg-white/10 text-white/60'
        }`}>
          {isActive ? (isPaused ? 'Paused' : 'Active') : 'Stopped'}
        </div>
      </div>

      {/* Timer display when active */}
      {isActive && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-white/60">Session ({adsShownThisSession}/{adsPerSession} ads)</span>
            </div>
            <span className={`font-mono text-lg font-bold ${isPaused ? 'text-yellow-400' : 'text-blue-400'}`}>
              {isPaused ? 'AD' : formatTime(timeRemaining)}
            </span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: isPaused ? '100%' : `${timerPercent}%` }}
              transition={{ duration: 0.5 }}
              className={`h-full rounded-full ${
                isPaused 
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 animate-pulse'
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500'
              }`}
            />
          </div>
        </div>
      )}

      {/* Energy bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="flex items-center gap-2">
            <Battery className="w-4 h-4 text-yellow-400" />
            <span className="text-white/60">Energy</span>
          </div>
          <span className="font-medium">{energy} / {maxEnergy}</span>
        </div>
        <div className="h-3 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${energyPercent}%` }}
            className={`h-full rounded-full ${
              energyPercent > 50 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                : energyPercent > 20
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                  : 'bg-gradient-to-r from-red-500 to-pink-500'
            }`}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <p className="text-white/60 text-xs mb-1">Today's Cycles</p>
          <p className="font-bold text-lg">{todayCycles}</p>
        </div>
        <div className="bg-white/5 rounded-xl p-3 text-center">
          <p className="text-white/60 text-xs mb-1">Session Earned</p>
          <p className="font-bold text-lg text-green-400">+{earnedThisSession}</p>
        </div>
      </div>

      {/* Energy depleted message with Watch Ad button */}
      {!isActive && energy < 1 && (
        <div className="mb-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
          <div className="flex items-start gap-3">
            <Battery className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-yellow-400 font-medium text-sm mb-1">No Energy Left!</p>
              <p className="text-white/60 text-xs mb-3">
                Watch a rewarded video ad to earn <span className="text-green-400 font-medium">+{energyPerAd} energy</span>, 
                or purchase an energy package with your points.
              </p>
              <div className="flex gap-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={watchAdForEnergy}
                  disabled={isWatchingAd}
                  className="flex-1 py-2 px-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isWatchingAd ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Gift className="w-4 h-4" />
                      Watch Ad +{energyPerAd}
                    </>
                  )}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={openPackageModal}
                  className="py-2 px-4 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Buy
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Low energy warning with Watch Ad button (when energy is low but not zero) */}
      {!isActive && energy >= 1 && energy <= 10 && (
        <div className="mb-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Battery className="w-4 h-4 text-orange-400" />
              <p className="text-orange-400 text-xs font-medium">Low energy!</p>
            </div>
            <div className="flex gap-2">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={watchAdForEnergy}
                disabled={isWatchingAd}
                className="py-1.5 px-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-medium flex items-center gap-1.5 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isWatchingAd ? (
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Gift className="w-3 h-3" />
                    +{energyPerAd}
                  </>
                )}
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={openPackageModal}
                className="py-1.5 px-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-medium flex items-center gap-1.5 hover:opacity-90 transition-opacity"
              >
                <ShoppingCart className="w-3 h-3" />
                Buy
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Control button */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={isActive ? stopAutoFaucet : startAutoFaucet}
        disabled={isLoading || (!isActive && energy < 1) || isPaused}
        className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 ${
          isActive
            ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
            : energy >= 1
              ? 'btn-primary'
              : 'bg-white/10 text-white/50 cursor-not-allowed'
        }`}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
        ) : isActive ? (
          <>
            <Pause className="w-5 h-5" />
            Stop Auto Faucet
          </>
        ) : (
          <>
            <Play className="w-5 h-5" />
            Start Auto Faucet
          </>
        )}
      </motion.button>

      {/* Info text */}
      <p className="text-white/40 text-xs text-center mt-3">
        Uses 1 energy per cycle • {rewardPerCycle} points per {sessionDuration}s • {adsPerSession} ads per session
      </p>

      {/* Always visible: Watch Ad and Buy buttons */}
      <div className="flex gap-2 mt-4">
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={watchAdForEnergy}
          disabled={isWatchingAd}
          className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:shadow-lg hover:shadow-green-500/50 transition-all disabled:opacity-50 active:shadow-none"
        >
          {isWatchingAd ? (
            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Gift className="w-3.5 h-3.5" />
              <span>Ad</span>
            </>
          )}
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={openPackageModal}
          className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:shadow-lg hover:shadow-blue-500/50 transition-all active:shadow-none"
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          <span>Buy</span>
        </motion.button>
      </div>

      {/* Energy Package Purchase Modal - Full Screen */}
      <AnimatePresence>
        {showPackageModal && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 bg-gradient-to-b from-gray-800 to-gray-900 z-50 flex flex-col"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowPackageModal(false);
            }}
          >
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-4 sticky top-0 z-10">
              <button
                onClick={() => setShowPackageModal(false)}
                className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
                >
                  <Zap className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <h3 className="font-bold text-lg text-white">Energy Packs</h3>
                  <p className="text-white/70 text-xs">Boost your earning power</p>
                </div>
              </div>
            </div>

            {/* Packages List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingPackages ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-10 h-10 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <Zap className="w-16 h-16 mx-auto mb-4 opacity-40" />
                  <p className="text-lg">No packages available</p>
                </div>
              ) : (
                packages.map((pkg) => {
                  const canAfford = user && user.balance >= pkg.price;
                  const isPurchasing = purchasingId === pkg._id;
                  
                  return (
                    <motion.div
                      key={pkg._id}
                      whileHover={{ scale: canAfford ? 1.02 : 1 }}
                      whileTap={{ scale: canAfford ? 0.98 : 1 }}
                      className={`relative p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-4 ${
                        canAfford
                          ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/50 hover:border-blue-400 shadow-lg'
                          : 'bg-gray-800/50 border-gray-700 opacity-60'
                      }`}
                    >
                      {/* Badge */}
                      {pkg.badge && (
                        <motion.div
                          initial={{ rotate: -45, y: -10 }}
                          animate={{ rotate: 0, y: 0 }}
                          className="absolute -top-3 -left-3 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-xs font-bold text-black shadow-lg"
                        >
                          {pkg.badge}
                        </motion.div>
                      )}

                      {/* Left Section - Icon and Info */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                          <Zap className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-white text-sm">{pkg.name}</h4>
                          <p className="text-green-400 font-bold text-lg">+{pkg.energyGranted}</p>
                          {pkg.description && (
                            <p className="text-white/60 text-xs truncate">{pkg.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Right Section - Price and Button */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-white/60 text-xs">Cost</p>
                          <p className="font-bold text-white">{pkg.price}</p>
                        </div>
                        <motion.button
                          whileTap={{ scale: 0.92 }}
                          onClick={() => purchasePackage(pkg)}
                          disabled={!canAfford || isPurchasing}
                          className={`py-2 px-4 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                            canAfford
                              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-lg shadow-blue-500/50'
                              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isPurchasing ? (
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <>
                              <ShoppingCart className="w-3.5 h-3.5" />
                              Buy
                            </>
                          )}
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
